import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatIcon from '@mui/icons-material/Chat';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { agentApi, Agent as AgentType } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAgentContext } from '../../context/AgentContext';

// Add a new interface for document metadata
interface DocumentMetadata {
  fileId: string;
  displayName: string;
  filename: string;
  mimetype: string;
  size: number | string; // Allow both number and string for size
  uploadDate: string;
}

// Add an interface for URL entries
interface UrlEntry {
  value: string;
}

// Helper function to safely get domain from URL string
const getDomainFromUrl = (urlString: string): string => {
  try {
    const url = new URL(urlString);
    return url.hostname || urlString;
  } catch (error) {
    return urlString;
  }
};

const AgentsPage: React.FC = () => {
  // State for agents list
  const [agents, setAgents] = useState<AgentType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // State for create/edit dialog
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAgent, setCurrentAgent] = useState<AgentType | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [urls, setUrls] = useState<UrlEntry[]>([{ value: '' }]);
  const [documents, setDocuments] = useState<string[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Add these state variables at the top along with other state variables
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');

  // State for file management
  const [files, setFiles] = useState<File[]>([]);

  // Update this to store more complete document metadata
  const [documentMetadata, setDocumentMetadata] = useState<
    Record<string, DocumentMetadata>
  >({});

  // Add state for document preview dialog
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentMetadata | null>(null);
  const [documentContent, setDocumentContent] = useState<string | null>(null);
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);

  // Add a new function to handle agent selection, inside the AgentsPage component
  // (after the existing functions but before the return statement)
  const navigate = useNavigate();
  const { setSelectedAgent } = useAgentContext();

  // Fetch agents on component mount
  useEffect(() => {
    fetchAgents();
  }, []);

  // Fetch all agents
  const fetchAgents = async () => {
    setLoading(true);
    try {
      const data = await agentApi.getAgents();
      setAgents(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
      setError('Failed to load agents. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle URL input changes
  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index].value = value;
    setUrls(newUrls);
  };

  // Function to add a new URL field
  const addUrlField = () => {
    setUrls([...urls, { value: '' }]);
  };

  // Function to remove a URL field
  const removeUrlField = (index: number) => {
    if (urls.length <= 1) return; // Keep at least one URL field
    const newUrls = urls.filter((_, i) => i !== index);
    setUrls(newUrls);
  };

  // Open dialog for creating a new agent
  const handleOpenCreate = () => {
    setEditMode(false);
    setName('');
    setDescription('');
    setUrls([{ value: '' }]);
    setDocuments([]);
    setUploadedFile(null);
    setFiles([]);
    setCurrentAgent(null);
    setOpen(true);
  };

  // Open dialog for editing an existing agent
  const handleOpenEdit = (agent: AgentType) => {
    setEditMode(true);
    setName(agent.name);
    setDescription(agent.description);

    // Handle siteUrl as either string or array
    if (Array.isArray(agent.siteUrl) && agent.siteUrl.length > 0) {
      // Map array of URLs to URL entries
      setUrls(agent.siteUrl.map((url) => ({ value: url })));
    } else if (typeof agent.siteUrl === 'string' && agent.siteUrl) {
      // Single URL string
      setUrls([{ value: agent.siteUrl }]);
    } else {
      // No URLs
      setUrls([{ value: '' }]);
    }

    setDocuments([...agent.documents]);
    setUploadedFile(null);
    setFiles([]);
    setDocumentMetadata({});
    setCurrentAgent(agent);
    setOpen(true);
  };

  // Close dialog
  const handleClose = () => {
    setOpen(false);
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFile = event.target.files[0];
      setUploadedFile(newFile);

      // Add file to the files array for later submission
      setFiles([...files, newFile]);

      // Also add filename to documents list for UI display
      setDocuments([...documents, newFile.name]);
    }
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(null);
  };

  // Submit form
  const handleSubmit = async () => {
    if (name.trim() === '' || description.trim() === '') {
      return; // Basic validation
    }

    setSubmitting(true);

    // Filter out empty URLs and extract values
    const validUrls = urls
      .map((entry) => entry.value.trim())
      .filter((url) => url !== '');

    const agentData: AgentType = {
      name,
      description,
      siteUrl: validUrls,
      documents: documents.filter(
        (doc) => !files.some((file) => file.name === doc)
      ), // Keep only document IDs, not filenames
    };

    try {
      if (editMode && currentAgent && currentAgent._id) {
        // Update existing agent with files
        await agentApi.updateAgent(currentAgent._id, agentData, files);
        setNotification({
          message: 'Agent updated successfully',
          type: 'success',
        });
      } else {
        // Create new agent with files
        await agentApi.createAgent(agentData, files);
        setNotification({
          message: 'Agent created successfully',
          type: 'success',
        });
      }

      // Reset files state
      setFiles([]);
      setUploadedFile(null);

      fetchAgents(); // Refresh the agents list
      handleClose();
    } catch (err) {
      console.error('Error saving agent:', err);
      setNotification({
        message: `Failed to ${editMode ? 'update' : 'create'} agent`,
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (id: string) => {
    setAgentToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  // Delete agent
  const confirmDelete = async () => {
    if (agentToDelete) {
      try {
        await agentApi.deleteAgent(agentToDelete);
        setNotification({
          message: 'Agent deleted successfully',
          type: 'success',
        });
        fetchAgents(); // Refresh the agents list
      } catch (err) {
        console.error('Error deleting agent:', err);
        setNotification({ message: 'Failed to delete agent', type: 'error' });
      }
      closeDeleteDialog();
    }
  };

  // Add this function to handle viewing the prompt
  const handleViewPrompt = (agent: AgentType) => {
    if (agent.systemPrompt) {
      setSelectedPrompt(agent.systemPrompt);
      setPromptDialogOpen(true);
    }
  };

  const handleClosePromptDialog = () => {
    setPromptDialogOpen(false);
  };

  // Function to select an agent and navigate to document editor
  const handleSelectAgent = (agent: AgentType) => {
    if (agent && agent._id) {
      // Navigate to chat with agent ID as URL parameter
      window.location.replace(`/chat?agent=${agent._id}`);
    }
  };

  // Update the fetchDocumentMetadata function to store the full metadata object
  useEffect(() => {
    if (
      open &&
      currentAgent &&
      currentAgent.documents &&
      currentAgent.documents.length > 0
    ) {
      // Fetch metadata for each document
      const fetchDocumentMetadata = async () => {
        const newDocumentMetadata: Record<string, DocumentMetadata> = {};

        await Promise.all(
          currentAgent.documents.map(async (docId) => {
            try {
              const metadata = await agentApi.getDocumentMetadata(docId);
              newDocumentMetadata[docId] = metadata;
            } catch (error) {
              console.error(
                `Failed to fetch metadata for document ${docId}:`,
                error
              );
              newDocumentMetadata[docId] = {
                fileId: docId,
                displayName: `Document ${docId.substring(0, 8)}...`,
                filename: `unknown_${docId.substring(0, 8)}`,
                mimetype: 'application/octet-stream',
                size: '0',
                uploadDate: new Date().toISOString(),
              };
            }
          })
        );

        setDocumentMetadata(newDocumentMetadata);
      };

      fetchDocumentMetadata();
    }
  }, [open, currentAgent]);

  // Function to get document display name (updated)
  const getDocumentDisplayName = (docId: string) => {
    // If it's a file name (from new upload), return the name as is
    if (files.some((file) => file.name === docId)) {
      return docId;
    }

    // If it's an existing document ID, look up the display name
    return (
      documentMetadata[docId]?.displayName || `Doc ${docId.substring(0, 8)}...`
    );
  };

  // Function to get document icon based on mimetype
  const getDocumentIcon = (docId: string) => {
    // For new files
    if (files.some((file) => file.name === docId)) {
      const file = files.find((f) => f.name === docId);
      if (file?.type?.includes('pdf')) {
        return <PictureAsPdfIcon />;
      }
      return <TextSnippetIcon />;
    }

    // For existing documents
    const metadata = documentMetadata[docId];
    if (metadata?.mimetype?.includes('pdf')) {
      return <PictureAsPdfIcon />;
    }
    return <TextSnippetIcon />;
  };

  // Add a function to handle document preview
  const handleDocumentPreview = async (docId: string) => {
    if (!documentMetadata[docId]) return;

    console.log('Preview document with ID:', docId);
    console.log('Document metadata:', documentMetadata[docId]);

    setSelectedDocument(documentMetadata[docId]);
    setDocumentPreviewOpen(true);
    setDocumentPreviewLoading(true);

    try {
      // Check if it's a PDF file first
      if (documentMetadata[docId].mimetype.includes('pdf')) {
        console.log('PDF file detected, displaying PDF message');
        setDocumentContent(
          'PDF content cannot be displayed directly. Please use the download button.'
        );
      } else {
        // Use the agentApi service to get the document content
        console.log('Getting content for document ID:', docId);
        const content = await agentApi.getDocumentContent(docId);
        console.log('Received content length:', content.length);
        if (content.startsWith('Error:')) {
          console.error('Error in content response:', content);
        }
        setDocumentContent(content);
      }
    } catch (error) {
      console.error('Failed to fetch document content:', error);
      setDocumentContent(
        'Error loading document content. Please try again later or download the file.'
      );
    } finally {
      setDocumentPreviewLoading(false);
    }
  };

  // Function to format file size
  const formatFileSize = (sizeInBytes: string): string => {
    const size = parseInt(sizeInBytes, 10);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          AI Agents
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Please select an AI agent to use in the document editor. Different
          agents have different capabilities based on their system prompts.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Agent
        </Button>
      </Box>

      {/* Error message if loading fails */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>System Prompt</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No agents found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((agent) => (
                  <TableRow key={agent._id}>
                    <TableCell>{agent.name}</TableCell>
                    <TableCell>{agent.description}</TableCell>
                    <TableCell>
                      {agent.siteUrl &&
                        (Array.isArray(agent.siteUrl) ? (
                          agent.siteUrl.length > 0 ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                maxWidth: '250px',
                              }}
                            >
                              {agent.siteUrl.map((url, index) => (
                                <Chip
                                  key={index}
                                  icon={<LinkIcon />}
                                  label={getDomainFromUrl(url)}
                                  onClick={() => window.open(url, '_blank')}
                                  size="small"
                                  sx={{ margin: '2px' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No URLs
                            </Typography>
                          )
                        ) : agent.siteUrl ? (
                          <Chip
                            icon={<LinkIcon />}
                            label={getDomainFromUrl(agent.siteUrl as string)}
                            onClick={() =>
                              window.open(agent.siteUrl as string, '_blank')
                            }
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No URLs
                          </Typography>
                        ))}
                    </TableCell>
                    <TableCell>
                      {agent.documents.length > 0 ? (
                        <Chip
                          icon={<DescriptionIcon />}
                          label={`${agent.documents.length} docs`}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No documents
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {agent.systemPrompt ? (
                        <Chip
                          icon={<ChatIcon />}
                          label="View Prompt"
                          onClick={() => handleViewPrompt(agent)}
                          size="small"
                          color="secondary"
                          sx={{
                            'borderRadius': '4px',
                            'fontWeight': 500,
                            'px': 1,
                            '&:hover': {
                              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                              backgroundColor: 'secondary.dark',
                            },
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No prompt
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpenEdit(agent)}
                        size="small"
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => agent._id && openDeleteDialog(agent._id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleSelectAgent(agent)}
                        size="small"
                        color="success"
                        title="Select this agent"
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Agent Creation/Edit Dialog - Update the document section */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {editMode ? 'Edit Agent' : 'Create New Agent'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">URLs</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addUrlField}
                  variant="outlined"
                >
                  Add URL
                </Button>
              </Box>
              <Stack spacing={2}>
                {urls.map((url, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                  >
                    <TextField
                      label={`URL ${index + 1}`}
                      fullWidth
                      value={url.value}
                      onChange={(e) => handleUrlChange(index, e.target.value)}
                      placeholder="https://example.com"
                    />
                    {urls.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => removeUrlField(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Documents
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="application/pdf,text/plain,text/markdown"
                  style={{ display: 'none' }}
                  id="document-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="document-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<DescriptionIcon />}
                  >
                    Upload Document
                  </Button>
                </label>
                {uploadedFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Uploaded: {uploadedFile.name}
                  </Typography>
                )}
              </Box>
              {documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Attached Documents:
                  </Typography>
                  <List
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {documents.map((doc, index) => {
                      // Check if this is a file name (from new upload) or a document ID (from database)
                      const isNewFile = files.some((file) => file.name === doc);
                      const displayName = getDocumentDisplayName(doc);
                      const documentIcon = getDocumentIcon(doc);

                      const documentSize = isNewFile
                        ? files.find((f) => f.name === doc)?.size
                        : documentMetadata[doc]?.size;

                      const formattedSize = documentSize
                        ? formatFileSize(documentSize.toString())
                        : 'Unknown size';

                      return (
                        <React.Fragment key={index}>
                          {index > 0 && <Divider />}
                          <ListItem>
                            <ListItemIcon>{documentIcon}</ListItemIcon>
                            <ListItemText
                              primary={displayName}
                              secondary={formattedSize}
                            />
                            <ListItemSecondaryAction>
                              {!isNewFile && (
                                <Tooltip title="Preview document">
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleDocumentPreview(doc)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove document">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    // Remove from documents array
                                    const newDocuments = documents.filter(
                                      (_, i) => i !== index
                                    );
                                    setDocuments(newDocuments);

                                    // If it's a new file, also remove from files array
                                    if (isNewFile) {
                                      setFiles(
                                        files.filter(
                                          (file) => file.name !== doc
                                        )
                                      );
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            {submitting ? 'Saving...' : editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        PaperProps={{
          sx: {
            bgcolor: '#121212',
            color: 'white',
            borderRadius: 2,
          },
        }}
      >
        <DialogContent>
          <DialogContentText sx={{ color: 'white' }}>
            Are you sure you want to delete this agent?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={closeDeleteDialog}
            sx={{
              'bgcolor': 'rgba(255, 255, 255, 0.2)',
              'color': 'white',
              'px': 3,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            sx={{
              'bgcolor': 'primary.main',
              'color': 'white',
              'px': 3,
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* System Prompt Dialog */}
      <Dialog
        open={promptDialogOpen}
        onClose={handleClosePromptDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { minHeight: '60vh' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
          <Typography variant="h5">System Prompt</Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            This AI-generated system prompt defines the agent's role and
            capabilities
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box
            sx={{
              p: 3,
              bgcolor: '#f8f9fa',
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              fontFamily: '"Roboto Mono", monospace',
              fontSize: '0.9rem',
              lineHeight: 1.6,
              border: '1px solid #e0e0e0',
              height: '100%',
              maxHeight: '40vh',
              overflow: 'auto',
            }}
          >
            {selectedPrompt}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', pt: 2, px: 3 }}>
          <Button onClick={handleClosePromptDialog} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(selectedPrompt);
              setNotification({
                message: 'Prompt copied to clipboard',
                type: 'success',
              });
            }}
            color="primary"
            variant="contained"
            startIcon={<ContentCopyIcon />}
          >
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Document Preview Dialog */}
      <Dialog
        open={documentPreviewOpen}
        onClose={() => setDocumentPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { minHeight: '70vh' },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {selectedDocument?.displayName || 'Document Preview'}
            </Typography>
            <Tooltip title="Download document">
              <IconButton
                onClick={() => {
                  if (selectedDocument) {
                    // Use the agentApi service to get the download URL
                    const downloadUrl = agentApi.getDocumentDownloadUrl(
                      selectedDocument.fileId
                    );
                    window.open(downloadUrl, '_blank');
                  }
                }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {documentPreviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedDocument?.mimetype?.includes('pdf') ? (
            <Box
              sx={{
                height: '60vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                p: 2,
                gap: 2,
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 60, color: '#d32f2f' }} />
              <Typography variant="h6">PDF Document</Typography>
              <Typography>
                PDF preview not available. Please download the document to view
                it.
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={() => {
                  if (selectedDocument) {
                    const downloadUrl = agentApi.getDocumentDownloadUrl(
                      selectedDocument.fileId
                    );
                    window.open(downloadUrl, '_blank');
                  }
                }}
              >
                Download PDF
              </Button>
            </Box>
          ) : documentContent && documentContent.startsWith('Error:') ? (
            <Box
              sx={{
                height: '60vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#fef6f6',
                borderRadius: 1,
                p: 2,
                gap: 2,
              }}
            >
              <Alert severity="error" sx={{ width: '100%', maxWidth: '500px' }}>
                {documentContent}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                There was a problem loading this document. You can try
                downloading it instead.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                bgcolor: '#f5f5f5',
                p: 3,
                borderRadius: 1,
                height: '60vh',
                overflow: 'auto',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                border: '1px solid #e0e0e0',
              }}
            >
              {documentContent}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error notification */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentsPage;
